import { useState } from "react";
import { authFetch } from "../api";

const AGE_GROUPS = [
  { value: "under_18", label: "Under 18" },
  { value: "18_30",    label: "18 – 30" },
  { value: "31_45",    label: "31 – 45" },
  { value: "46_60",    label: "46 – 60" },
  { value: "60_plus",  label: "60 +" },
];

const GENDERS = [
  { value: "male",   label: "Male" },
  { value: "female", label: "Female" },
  { value: "other",  label: "Other" },
];

export default function FootfallEntry({ center, userEmail, apiBase, onClose }) {
  const [gender, setGender]       = useState(null);
  const [ageGroup, setAgeGroup]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [flash, setFlash]         = useState(null);   // { type: "ok" | "err", text }
  const [todayCount, setTodayCount] = useState(null);

  const localToday = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local tz
  const tzOffset   = -new Date().getTimezoneOffset();        // minutes east of UTC (e.g. 330 for IST)

  // Load today's count on mount
  useState(() => {
    authFetch(`${apiBase}/centers/${center.id}/footfall/summary?from_date=${localToday}&tz_offset_minutes=${tzOffset}`)
      .then(r => r.json())
      .then(d => setTodayCount(d.total ?? 0))
      .catch(() => {});
  });

  const handleLog = async () => {
    if (!gender || !ageGroup) return;
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/centers/${center.id}/footfall`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ age_group: ageGroup, gender, local_date: localToday }),
      });
      if (!res.ok) throw new Error("Failed to save");
      setFlash({ type: "ok", text: "Entry logged!" });
      setTodayCount(c => (c ?? 0) + 1);
      setGender(null);
      setAgeGroup(null);
      setTimeout(() => setFlash(null), 2000);
    } catch {
      setFlash({ type: "err", text: "Error — try again" });
      setTimeout(() => setFlash(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = gender && ageGroup && !saving;

  const isModal = !!onClose;

  return (
    <div style={isModal ? styles.overlay : {}}>
      <div style={isModal ? styles.card : styles.cardInline}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Log Entry</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>{center.name}</div>
          </div>
          {onClose && (
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          )}
        </div>

        {/* Today counter */}
        {todayCount !== null && (
          <div style={styles.counter}>
            <span style={{ fontSize: 32, fontWeight: 700, color: "#0093dd" }}>{todayCount}</span>
            <span style={{ fontSize: 13, color: "#555", marginLeft: 8 }}>entries today</span>
          </div>
        )}

        {/* Flash message */}
        {flash && (
          <div style={{
            ...styles.flash,
            background: flash.type === "ok" ? "#e0f3fc" : "#fdecea",
            color:      flash.type === "ok" ? "#004d74" : "#8B2020",
          }}>
            {flash.text}
          </div>
        )}

        {/* Gender */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Gender</div>
          <div style={styles.btnRow}>
            {GENDERS.map(g => (
              <button
                key={g.value}
                onClick={() => setGender(g.value)}
                style={choiceBtn(gender === g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Age group */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Age Group</div>
          <div style={styles.btnRow}>
            {AGE_GROUPS.map(a => (
              <button
                key={a.value}
                onClick={() => setAgeGroup(a.value)}
                style={choiceBtn(ageGroup === a.value)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleLog}
          disabled={!canSubmit}
          style={styles.logBtn(canSubmit)}
        >
          {saving ? "Saving…" : "Log Entry"}
        </button>

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16,
  },
  card: {
    background: "#fff", borderRadius: 16,
    width: "100%", maxWidth: 420,
    padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  cardInline: {
    background: "#fff", borderRadius: 16,
    width: "100%", padding: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 20,
    color: "#1a1a1a",
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 18, cursor: "pointer", color: "#888",
    padding: "0 4px", lineHeight: 1,
  },
  counter: {
    display: "flex", alignItems: "baseline",
    background: "#f5faff", borderRadius: 10,
    padding: "12px 16px", marginBottom: 20,
  },
  flash: {
    borderRadius: 8, padding: "10px 14px",
    marginBottom: 16, fontSize: 14, fontWeight: 500,
    textAlign: "center",
  },
  section: { marginBottom: 20 },
  sectionLabel: {
    fontSize: 13, fontWeight: 600, color: "#444",
    marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em",
  },
  btnRow: {
    display: "flex", flexWrap: "wrap", gap: 8,
  },
  logBtn: (active) => ({
    width: "100%", padding: "14px 0",
    background: active ? "#0093dd" : "#d0d0d0",
    color: "#fff", border: "none",
    borderRadius: 10, fontSize: 16, fontWeight: 700,
    cursor: active ? "pointer" : "not-allowed",
    marginTop: 4, transition: "background 0.15s",
  }),
};

function choiceBtn(selected) {
  return {
    padding: "10px 18px",
    borderRadius: 8,
    border: selected ? "2px solid #0093dd" : "2px solid #e0e0e0",
    background: selected ? "#e5f5ff" : "#fafafa",
    color: selected ? "#004d74" : "#333",
    fontWeight: selected ? 700 : 400,
    fontSize: 14, cursor: "pointer",
    transition: "all 0.1s",
  };
}
